import { Close } from '@mui/icons-material'
import { Button, ButtonProps, styled } from '@mui/material'
import Image from 'next/image'
import { ReactNode } from 'react'
import { isDefined } from '@omnea-app/legacy/utils/type-guard/isDefined'

interface ButtonTextColours {
  selected: string
  active: string
  inactive: string
  hover: string
}

interface ButtonBackgroundColours {
  selected: string
  active: string
  inactive: string
  hover: string
}

interface ToggleButtonColours {
  backgroundColours?: Partial<ButtonBackgroundColours>
  textColours?: Partial<ButtonTextColours>
}

const variantToggleButtonColours: ToggleButtonColours = {
  backgroundColours: {
    selected: 'neutralLight.0',
    inactive: 'neutralDark.100',
    hover: 'neutralDark.50',
  },
  textColours: {
    selected: 'neutralDark.700',
    inactive: 'neutralLight.300',
    hover: 'neutralLight.0',
  },
}

const defaultToggleButtonColours: ToggleButtonColours = {
  backgroundColours: {
    active: 'neutralLight.500',
    inactive: 'neutralLight.0',
  },
  textColours: {
    selected: 'neutralLight.0',
    inactive: 'neutralDark.700',
    hover: 'neutralDark.700',
  },
}

export interface Props extends ButtonProps {
  label: ReactNode
  onClick: () => void
  isSelected?: boolean
  onDelete?: () => void
  variantToggleButton?: boolean
  icons?: string[]
}

const StyleButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(2),
}))

export const ToggleButton = ({
  label,
  onClick,
  isSelected = false,
  onDelete,
  variantToggleButton,
  children,
  icons,
  ...buttonProps
}: Props) => {
  const colours: ToggleButtonColours = variantToggleButton ? variantToggleButtonColours : defaultToggleButtonColours

  const backgroundSelected = colours.backgroundColours?.selected
  const backgroundActive = colours.backgroundColours?.active
  const backgroundInactive = colours.backgroundColours?.inactive
  const backgroundHover = colours.backgroundColours?.hover

  const textActive = colours.textColours?.active
  const textInActive = colours.textColours?.inactive
  const textSelected = colours.textColours?.selected
  const textHover = colours.textColours?.hover

  const iconElement = isDefined(icons)
    ? icons.map((src, index) => <Image key={`${src}-${index}`} src={src} alt="integration icon" width={20} height={20} />)
    : null
  return (
    <StyleButton
      variant={isSelected ? 'contained' : 'outlined'}
      color="neutralDark"
      onClick={onClick}
      sx={{
        ...(variantToggleButton ? {} : { borderColor: '#E7E7ED' }),
        backgroundColor: !isSelected ? backgroundInactive : backgroundSelected,
        color: !isSelected ? textInActive : textSelected,
        minHeight: '30px',
        height: '30px',
        display: 'flex',
        gap: 2,
        '&:active': {
          background: backgroundActive,
          color: textActive,
        },
        '&:hover': {
          cursor: !isSelected ? 'pointer' : 'default',
          backgroundColor: !isSelected ? backgroundHover : backgroundSelected,
          color: !isSelected ? textHover : textSelected,
        },
      }}
      {...buttonProps}
    >
      {iconElement}
      {label}
      {children}
      {isSelected && onDelete && <Close onClick={onDelete} sx={{ fontSize: 16, color: 'neutralLight.700' }} />}
    </StyleButton>
  )
}
